import React, { useEffect, useState, useCallback } from 'react';
import './App.css'; // Ensure you add your styles here
import Header from './Header';
import Features from './Features';
import Footer from './Footer';
import Head from './Head';
//import Footer from './Footer';
import ReactGA from 'react-ga4';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import i18n from './i18n';
import { PiCopy } from 'react-icons/pi';
import { IoIosArrowBack } from 'react-icons/io';
import Lottie from 'react-lottie';
import waiting from './assets/lottie/waiting1.json';
import errorLottie from './assets/lottie/error.json';
import { QRCodeSVG } from 'qrcode.react';
import { IoQrCodeOutline } from 'react-icons/io5';
import { IoMdClose } from 'react-icons/io';
import { SlRefresh } from 'react-icons/sl';
import { jwtDecode } from 'jwt-decode';
import { RiAiGenerate } from 'react-icons/ri';
import alertImage from './assets/png/alert.png';
import { useTranslation } from 'react-i18next';

function usePageTracking() {
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: 'Home',
    });
  }, []);
}

function registerGAevent(aCategory, value) {
  ReactGA.event({
    category: aCategory,
    action: value,
  });
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie =
    name +
    '=' +
    encodeURIComponent(value) +
    '; expires=' +
    expires +
    '; path=/';
}

// Helper function to get relative time
function timeAgo(date) {
  const now = new Date();
  const diffInSeconds = Math.floor((now - new Date(date)) / 1000);

  const minutes = Math.floor(diffInSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return days === 1 ? '1 day ago' : `${days} days ago`;
  } else if (hours > 0) {
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  } else if (minutes > 0) {
    return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  } else {
    return 'just now';
  }
}

const checkTokenExpiration = (
  token,
  setEmailExpired,
  setTokenMessage,
  setTokenColor
) => {
  if (!token) {
    setTokenMessage('No token found.');
    setTokenColor('black');
    return;
  }

  try {
    const decoded = jwtDecode(token);
    const expirationTime = decoded.exp * 1000;
    const currentTime = Date.now();
    const timeLeft = expirationTime - currentTime;

    const formatTime = (time) => {
      const days = Math.floor(time / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (time % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((time % (1000 * 60 * 60)) / (1000 * 60));

      // Create a time string without using pluralization
      const timeParts = [];

      if (days > 0) timeParts.push(`${days} ${i18n.t('days')}`);
      if (hours > 0) timeParts.push(`${hours} ${i18n.t('hours')}`);
      if (minutes > 0) timeParts.push(`${minutes} ${i18n.t('minutes')}`);

      // If no time left, show 'just now'
      if (timeParts.length === 0) return i18n.t('justNow');

      // Join the time parts
      return timeParts.join(', ');
    };

    if (timeLeft > 0) {
      setEmailExpired(false);
      setTokenMessage(`${i18n.t('Emailexpiresin')} ${formatTime(timeLeft)}`);
      setTokenColor('var(--gray-color)');
    } else {
      setEmailExpired(true);
      setTokenMessage(i18n.t('Emailhasexpired'));
      setTokenColor('red');
    }
  } catch (error) {
    setTokenMessage(`Error decoding token: ${error.message}`);
    setTokenColor('red');
  }
};

const App = ({ lang }) => {
  usePageTracking();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailContent, setEmailContent] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Snackbar state
  const [snackbarMessage, setSnackbarMessage] = useState(''); // New state for snackbar message
  const refreshInterval = 5000; // Fetch emails every 5 seconds
  const [isFlipping, setIsFlipping] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [qrOpen, setQrOpen] = useState(false); // New state for QR code tooltip
  const [tooltipMessage, setTooltipMessage] = useState();
  const [open, setOpen] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [emailExpired, setEmailExpired] = useState(false);

  const { i18n } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  const handleOpenGenerate = () => {
    setDialogType('generate');
    setOpen(true);
  };

  const handleOpenDelete = () => {
    setDialogType('delete');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const getInitials = (name) => {
    if (!name) return '';

    const words = name.split(' ');
    const initials = words.length > 1 ? words[0][0] + words[1][0] : words[0][0];

    return initials.toUpperCase();
  };

  const fetchEmails = useCallback(async () => {
    if (!token || emailExpired) {
      return;
    }
    setIsSpinning(true);

    try {
      const response = await fetch(
        'https://api.onetimeinbox.com/tv1/exec/GetEmails',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            token: token,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        console.error('API error:', data.error);
      } else {
        setEmails((prevEmails) => {
          const newEmails = data.emails || [];
          const existingEmailIds = prevEmails.map((emailItem) => emailItem.id);

          // Filter out existing emails
          const filteredNewEmails = newEmails.filter(
            (emailItem) => !existingEmailIds.includes(emailItem.id)
          );

          // Combine previous emails with the new ones
          const combinedEmails = [...prevEmails, ...filteredNewEmails];

          // Sort combined emails by date in descending order
          combinedEmails.sort(
            (a, b) => new Date(b.created) - new Date(a.created)
          );

          // Return the updated emails array
          return combinedEmails;
        });
      }
    } catch (error) {
      console.error('Error fetching emails:', error);
    } finally {
      // Wait 2 seconds before setting isSpinning to false
      setTimeout(() => {
        setIsSpinning(false);
      }, 2000);
    }
  }, [token, emailExpired]);

  const fetchEmail = useCallback(async () => {
    try {
      const previousID = getCookie('emailID'); // Store the previous email
      // const emailCookie = getCookie('email');
      const response = await fetch(
        'https://api.onetimeinbox.com/tv1/GenerateEmail',
        {
          headers: {
            token: 'public',
            previd: previousID,
          },
        }
      );
      setTimeout(() => {
        setIsFlipping(false);
      }, 2000);
      const data = await response.json();
      setToken(data.token);
      setEmail(data.email);
      setCookie('token', data.token, 7);
      setCookie('email', data.email, 7);
      setCookie('emailID', data.id, 7);
      registerGAevent('Email generated', data.email);
      setEmailExpired(false);
      // // Set snackbar message and open it
      // if (emailCookie) {
      //   setSnackbarOpen(true);
      // }
    } catch (error) {
      console.error('Error fetching email:', error);
    }
  }, []);

  const fetchEmailContent = async (emailItem) => {
    try {
      const response = await fetch(emailItem.path);
      if (!response.ok) {
        throw new Error(`Error fetching email content: ${response.status}`);
      }
      // const content = await response.text();
      // const sanitizedContent = DOMPurify.sanitize(content);
      // const parser = new DOMParser();
      // const doc = parser.parseFromString(sanitizedContent, 'text/html');

      const content = await response.text();

      // Parse the content directly without sanitization
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');

      const links = doc.querySelectorAll('a');
      links.forEach((link) => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });

      const updatedContent = doc.documentElement.outerHTML;
      setEmailContent(updatedContent);
    } catch (error) {
      console.error('Error fetching email content:', error);
    }
  };

  useEffect(() => {
    // Check if URL contains parameters
    const {
      token: urlToken,
      email: urlEmail,
      emailID: urlEmailID,
    } = getUrlParameters();

    if (urlToken && urlEmail && urlEmailID) {
      // Set cookies from URL parameters
      setCookie('token', urlToken, 7);
      setCookie('email', urlEmail, 7);
      setCookie('emailID', urlEmailID, 7);
      setToken(urlToken);
      setEmail(urlEmail);
      fetchEmails();

      const url = new URL(window.location);
      url.searchParams.delete('token');
      url.searchParams.delete('email');
      url.searchParams.delete('emailID');
      window.history.replaceState({}, document.title, url);

      const intervalId = setInterval(() => {
        fetchEmails();
      }, refreshInterval);

      return () => clearInterval(intervalId);
    } else {
      const existingToken = getCookie('token');
      const existingEmail = getCookie('email');

      if (existingToken && existingEmail) {
        setToken(existingToken);
        setEmail(existingEmail);
        fetchEmails();

        const intervalId = setInterval(() => {
          fetchEmails();
        }, refreshInterval);

        return () => clearInterval(intervalId);
      } else {
        fetchEmail(); // Fetch new email if no cookies found
      }
    }
  }, [fetchEmails, fetchEmail]);

  const handleGenerate = () => {
    setEmails([]);
    setEmailContent('');
    setIsFlipping(true);
    fetchEmail();
    setIsButtonDisabled(true);
    setTimeout(() => {
      setIsButtonDisabled(false); // Re-enable the button after 5 seconds
    }, 5000);
    handleClose();
  };

  const handleViewEmail = (emailItem) => {
    setSelectedEmail(emailItem);
    fetchEmailContent(emailItem);
  };

  const handleBackToList = () => {
    setSelectedEmail(null);
    setEmailContent('');
  };

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(email)
      .then(() => {
        setSnackbarMessage(`${i18n.t('Copiedtoclipboard')}: ${email}`);

        // setTooltipMessage(`Copied to clipboard: ${email}`);
        setSnackbarOpen(true); // Show Snackbar on successful copy
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
      });
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false); // Close the Snackbar
  };
  const handleTooltipClose = () => {
    setTooltipMessage(i18n.t('Copytoclipboard'));
  };
  const defaultOptions = {
    loop: true, // Set to false to play once
    autoplay: true, // Starts playing automatically
    animationData: waiting,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice', // Adjusts the rendering of the animation
    },
  };

  const errorOptions = {
    loop: true,
    autoplay: true,
    animationData: errorLottie,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const generateEmailShareLink = () => {
    // Create a URL with token, email, and emailID as parameters
    const url = `${window.location.origin}?token=${encodeURIComponent(
      getCookie('token')
    )}&email=${encodeURIComponent(
      getCookie('email')
    )}&emailID=${encodeURIComponent(getCookie('emailID'))}`;
    return url;
  };

  const handleQrToggle = () => {
    setQrOpen(!qrOpen); // Toggle the QR code display
  };

  const getUrlParameters = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      token: params.get('token'),
      email: params.get('email'),
      emailID: params.get('emailID'),
    };
  };

  const isMobile = window.innerWidth <= 768;

  const [tokenMessage, setTokenMessage] = useState('');
  const [tokenColor, setTokenColor] = useState('black');

  useEffect(() => {
    const intervalId = setInterval(() => {
      checkTokenExpiration(
        token,
        setEmailExpired,
        setTokenMessage,
        setTokenColor
      );
    }, refreshInterval);
    return () => clearInterval(intervalId);
  }, [token]);

  useEffect(() => {
    // Call checkTokenExpiration whenever the token changes
    checkTokenExpiration(
      token,
      setEmailExpired,
      setTokenMessage,
      setTokenColor
    );
  }, [token]);

  return (
    <div className="content">
      <Head lang={lang} />;<div className="banner"></div>
      <div className="container pt-16 pt-md-18 pb-9">
        <Header />
        <div className="text-center email-form mt-4 pt-2">
          <div className="mb-0">
            {emailExpired && (
              <img
                src={alertImage}
                alt="Email Expired"
                className="expired-image"
                style={{ marginBottom: '10px' }} // Add any necessary styling here
              />
            )}
            <div className="your-temp-mail">
              {i18n.t('YourTemporaryEmailAddress')}{' '}
            </div>
            <input
              type="text"
              className={`email-input border rounded-pill ${
                isFlipping ? 'fade-animation' : ''
              } ${emailExpired ? 'expired' : ''}`}
              placeholder="Generate a temporary email address"
              disabled
              value={email}
            />
            <div className="expired-time" style={{ color: tokenColor }}>
              {tokenMessage}
            </div>
            <Tooltip
              title={tooltipMessage}
              onClose={handleTooltipClose}
              placement="bottom">
              <button
                onClick={copyToClipboard}
                className={`btn btn-pills btn-primary ${
                  isMobile ? 'pill-radius' : ''
                }`}>
                <PiCopy size={22} />
              </button>
            </Tooltip>
            <>
              {!isMobile && (
                <>
                  <Tooltip
                    title={
                      qrOpen ? (
                        <>
                          <QRCodeSVG
                            value={generateEmailShareLink()}
                            style={{ margin: '10px' }}
                            size={214}
                          />
                          <div
                            style={{
                              marginBottom: '10px',
                              textAlign: 'center',
                              color: 'black',
                              fontSize: '15px',
                            }}>
                            {i18n.t('Sharetomobiledevice')}
                          </div>
                        </>
                      ) : (
                        'Share via QR code'
                      )
                    }
                    classes={{ tooltip: 'qr-tooltip' }}
                    open={qrOpen}
                    onClose={handleQrToggle}
                    placement="top">
                    <button
                      onMouseEnter={handleQrToggle} // Show QR code on hover
                      onMouseLeave={handleQrToggle} // Hide QR code when hover ends
                      className="btn btn-pills-qr btn-primary">
                      <IoQrCodeOutline size={22} />
                    </button>
                  </Tooltip>
                </>
              )}
              <div className="group-buttons d-flex flex-wrap justify-content-between">
                <button
                  className="btn btn-primary btn-lg flex-grow-1 mx-2 my-2 d-flex align-items-center justify-content-center new-button btn-main"
                  style={{ fontSize: '0.895rem' }} // Adjust the font size (smaller than default)
                  disabled={isButtonDisabled}
                  onClick={handleOpenGenerate}>
                  <RiAiGenerate size={30} style={{ marginRight: '8px' }} />
                  {i18n.t('New')}
                </button>

                <button
                  className="btn btn-primary btn-lg flex-grow-1 mx-2 my-2 d-flex align-items-center justify-content-center dellete-button btn-main"
                  style={{ fontSize: '0.895rem' }} // Adjust the font size (smaller than default)
                  disabled={isButtonDisabled}
                  onClick={handleOpenDelete}>
                  <IoMdClose size={30} style={{ marginRight: '8px' }} />
                  {i18n.t('Delete')}
                </button>

                <button
                  className="btn btn-primary btn-lg flex-grow-1 mx-2 my-2 d-flex align-items-center justify-content-center btn-main"
                  style={{ fontSize: '0.895rem' }} // Adjust the font size (smaller than default)
                  onClick={copyToClipboard}>
                  <PiCopy size={30} style={{ marginRight: '8px' }} />
                  {i18n.t('Copy')}
                </button>
              </div>
            </>
          </div>
        </div>

        {selectedEmail ? (
          <div className="email-view main-container-email">
            <div
              className="head flex items-center text-white p-3 px-7 lg:rounded-t-md"
              style={{
                backgroundColor: ' #159687',
                borderRadius: '10px 10px 0 0',
                height: '60px',
              }}>
              <div
                className="w-full flex justify-between items-center"
                onClick={handleBackToList}
                style={{ cursor: 'pointer' }}>
                <IoIosArrowBack size={25} />
                <span className="ml-2">{i18n.t('GoBack')} </span>
              </div>
            </div>
            <div className="flex justify-between items-center py-4 px-7">
              <div className="email-info d-flex align-items-center">
                <div className="circle">
                  {getInitials(selectedEmail.sendername)}
                </div>
                <div className="ms-2">
                  {selectedEmail.sendername}
                  <div className="text-xs overflow-ellipsis">
                    {' '}
                    {selectedEmail.senderemail}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs overflow-ellipsis">
                  {new Date(selectedEmail.created).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </div>
              </div>
            </div>
            <div class="border-t border-b border-dashed py-4 px-7">
              {selectedEmail.subject}
            </div>
            {/* <div
            className="email-content main-container-email-content"
            dangerouslySetInnerHTML={{ __html: emailContent }}
          /> */}
            <iframe
              className="email-content main-container-email-content"
              srcDoc={emailContent}
              sandbox="allow-same-origin allow-scripts allow-popups"
              style={{ width: '100%', height: '1000px', border: 'none' }}
              title="Email Content"
            />
          </div>
        ) : (
          <div className="email-list">
            <div className="layout-header d-flex justify-content-between align-items-center p-4">
              <div className="text-left"> {i18n.t('Inbox')}</div>
              {!emailExpired && (
                <button
                  type="button"
                  onClick={fetchEmails}
                  disabled={isSpinning}
                  className="btn d-flex align-items-center color-white no-border">
                  <SlRefresh
                    className={`me-3 ${isSpinning ? 'spin' : ''}`}
                    size={22}
                  />
                  {i18n.t('refresh')}
                </button>
              )}
            </div>

            {emails.length > 0 ? (
              <TableContainer component={Paper} style={{ minHeight: '600px' }}>
                <Table aria-label="simple table">
                  <TableHead className="d-none">
                    <TableRow>
                      <TableCell></TableCell> {/* Add header for Icon */}
                      <TableCell>Sender</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emails.map((emailItem) => (
                      <TableRow
                        key={emailItem.id}
                        hover
                        onClick={() => handleViewEmail(emailItem)}
                        style={{ cursor: 'pointer', minHeight: '80px' }}>
                        {' '}
                        {/* Increase row height */}
                        <TableCell
                          style={{
                            paddingLeft: '22px',
                            width: '48px',
                          }}>
                          <div className="circle">
                            {getInitials(emailItem.sendername)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <>
                            {emailItem.sendername}
                            <br />
                            {emailItem.senderemail}
                          </>
                        </TableCell>
                        <TableCell>{emailItem.subject}</TableCell>
                        <TableCell>{timeAgo(emailItem.created)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <div className="main-container">
                <div className="inner-container">
                  {emailExpired ? (
                    <>
                      <p className="waiting-email">
                        {i18n.t('EmailhasexpiredPlease')}
                      </p>
                      <Lottie
                        options={errorOptions}
                        height={200}
                        width={200}
                        style={{ marginBottom: '30px' }}
                      />
                    </>
                  ) : (
                    <>
                      <p className="waiting-email">
                        {i18n.t('Waitingforincomingmail')}
                      </p>
                      <Lottie
                        options={defaultOptions}
                        height={200}
                        width={200}
                        style={{ marginBottom: '30px' }}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          onClose={handleCloseSnackbar}>
          <Alert onClose={handleCloseSnackbar} severity="success">
            {snackbarMessage} {/* Show generated email in Snackbar */}
          </Alert>
        </Snackbar>
      </div>
      {/* {!isMobile && <Footer />} */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          {dialogType === 'generate' ? i18n.t('Alert') : i18n.t('Warning')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogType === 'generate' ? (
              <>
                {i18n.t('Generatinganewemaiwill')}
                <br />
                {i18n.t('Doyouwanttocontinue')}
              </>
            ) : (
              <>
                {i18n.t('Deletingthisemail')}
                <br />
                {i18n.t('Doyouwanttocontinue')}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {i18n.t('CANCEL')}
          </Button>
          <Button
            onClick={
              dialogType === 'generate' ? handleGenerate : handleGenerate
            }
            color="secondary">
            {i18n.t('OK')}
          </Button>
        </DialogActions>
      </Dialog>
      <Features />
      <Footer />
    </div>
  );
};

export default App;
